import React, { useMemo } from 'react'
import { useChartExport } from '../../hooks/useChartExport'
import type { FfpDashboardData, MoneySplit } from '../../types'
import { formatRands, longMonth } from '../../utils'
import { Card } from '../Card'
import { SplitBars } from '../charts/SplitBars'
import { Reveal } from '../Reveal'
import { excludedChip, MONEY_PARTS, moneySplits } from '../../copy'
import styles from './health.module.sass'
import { Meaning, Metric } from '../CardParts'
import { NoFigures } from './parts'

type Props = {
  data: FfpDashboardData
  highlighted: boolean
  onAbout: () => void
}

export const MoneyCard = ({ data, highlighted, onAbout }: Props) => {
  const { money } = data.health
  const { latest } = money
  const { chartRef, exportImage } = useChartExport<'bar'>()
  const splits = useMemo(() => moneySplits(money.first, money.latest), [money.first, money.latest])
  const segments = useMemo(
    () =>
      MONEY_PARTS.map((part) => ({
        name: part.name,
        colour: part.colour,
        text: part.text,
        values: splits.map((split) => split[part.key]),
        captions: splits.map((split) => formatRands(split[part.key])),
      })),
    [splits]
  )
  const rows = useMemo(() => splits.map((split) => longMonth(split.month)), [splits])

  const title =
    latest && latest.debt > 0 && latest.investments > 0
      ? `For a typical person, of every R100 of pay before tax, ${formatRands(latest.debt)} goes to debt and ${formatRands(latest.investments)} is invested`
      : 'Where a typical person’s pay goes'
  const footerParts = [data.organization.name, latest ? longMonth(latest.month) : null, `n = ${money.people} of ${data.people.onboarded} people`]
  const footer = { text: footerParts.filter(Boolean).join(' · '), chip: excludedChip(money.excluded) }

  const body = latest ? <SplitBars ref={chartRef} rows={rows} segments={segments} rowHeight={60} label="Where every R100 of pay before tax goes, in the first and latest month" /> : <NoFigures text="No figures yet." />
  const metric = latest ? <Metric text={`Average share of pay before tax, per person · ${longMonth(latest.month)}`} /> : null

  return (
    <Reveal>
      <Card question="Where is your team’s money going?" title={title} onAbout={onAbout} highlighted={highlighted} exportImage={latest ? exportImage : undefined} exportName="Where your team’s money goes" footer={footer}>
        {metric}
        {body}
        <MoneyLegend latest={latest} />
        <Meaning text="We work out each person’s share of pay before tax, then average those shares. Spending already includes debt repayments and investing, so the shares overlap. The three cards below follow debt, spending and investing month by month." />
      </Card>
    </Reveal>
  )
}

// "R58 · Everyday spending" over "Rent, food, transport", for the latest month.
const MoneyLegend = ({ latest }: { latest: MoneySplit | null }) => {
  if (!latest) return null

  const items = MONEY_PARTS.map((part) => (
    <div key={part.key} className={styles.legendItem}>
      <span className={styles.swatch} style={{ background: part.colour }} />
      <div className={styles.legendText}>
        <span className={styles.legendName}>
          {formatRands(latest[part.key])} · {part.name}
        </span>
        <span className={styles.legendSub}>{part.sub}</span>
      </div>
    </div>
  ))

  return <div className={styles.legend}>{items}</div>
}
